import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ReactGA from 'react-ga4';
import { ArrowLeft, CheckCircle2, Cloud, Search, Tag, MessageSquare, Brain, Users, Target, TrendingUp, Video } from 'lucide-react';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';

const SmartJournals = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        window.scrollTo(0, 0);
        try {
            ReactGA.send({ hitType: 'pageview', page: '/products/smart-journals', title: 'Smart Journals' });
        } catch { }
    }, []);

    const videoManagementFeatures = [
        {
            title: 'Organization',
            description: 'Secure cloud-based video repository where all training and match videos are stored in one place.',
            icon: Cloud,
            color: 'from-emerald to-emerald/80'
        },
        {
            title: 'Searchability',
            description: 'Easily search, tag, bookmark, and organize videos. Search by tag names, video titles, and dates.',
            icon: Search,
            color: 'from-purple-500 to-purple-600'
        },
        {
            title: 'Capture Workflow',
            description: 'Record sessions using any smartphone, with automatic upload and organization within your journal.',
            icon: Video,
            color: 'from-blue-500 to-blue-600'
        }
    ];

    const interactivityFeatures = [
        {
            title: 'Conversational Insights',
            description: 'Ask VISIST specific questions about your performance and get instant, data-backed answers.',
            icon: MessageSquare
        },
        {
            title: 'Visual Answers',
            description: 'Receive answers backed by visuals and metrics, pulling data directly from your processed videos.',
            icon: Brain
        },
        {
            title: 'Analysis Integration',
            description: 'Integrates with Smart Insights to display heatmaps, tracking trails, occupancy data, and rally breakdowns.',
            icon: TrendingUp
        }
    ];

    const coachingFeatures = [
        {
            title: 'Feedback Tools',
            description: 'Coaches can leave intelligent feedback directly within videos, with AI-assisted coaching recommendations.',
            icon: Target
        },
        {
            title: 'Smarter Training Decisions',
            description: 'Access to daily video data enables data-backed training decisions without manual review of 50+ students.',
            icon: Users
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-deep-charcoal">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 py-20 relative z-10">
                    <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => navigate('/products')}
                        className="flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-semibold">Back to Products</span>
                    </motion.button>

                    <div className="max-w-5xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight uppercase text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">
                                Smart Journals
                            </h1>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-4xl mx-auto mb-8"
                        >
                            Your centralized, cloud-based performance hub that transforms training videos into a lifelong learning tool
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="text-lg text-gray-400 max-w-3xl mx-auto mb-12"
                        >
                            The "memory" of the VISIST.AI platform—capturing, analyzing, and storing every training session
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                        >
                            <button
                                onClick={() => dispatch(setIsLoginDialog(true))}
                                className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                            >
                                Sign In
                            </button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Centralized Video Management Section */}
            <section className="relative py-24 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-emerald/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-sm md:text-base font-bold uppercase tracking-widest mb-3" style={{ color: '#247E45' }}>
                            Never Lose a Session
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
                            Centralized <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Video Management</span>
                        </h2>
                        <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
                            Solving the problem of videos scattered across devices and platforms
                        </p>
                    </motion.div>

                    {/* Video Management Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Centralized.png"
                            alt="Smart Journals Centralized Video Management - Organization, Search, and Capture"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-200"
                        />
                    </motion.div>

                    {/* Management Features */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                        {videoManagementFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-emerald hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                                >
                                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-br ${feature.color}`}>
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 mb-4">{feature.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Search Examples */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-16 max-w-4xl mx-auto bg-gradient-to-br from-emerald/10 to-white border-2 border-emerald/30 rounded-2xl p-8"
                    >
                        <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                            <Tag className="w-8 h-8 text-emerald" />
                            Search Examples
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <p className="text-gray-700 font-semibold">"Forehand Smash"</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <p className="text-gray-700 font-semibold">"Air Toss Practice"</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <p className="text-gray-700 font-semibold">"Jan 15, 2024"</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* AI-Powered Interactivity Section */}
            <section className="relative py-24 bg-deep-charcoal overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">
                            More Than Storage
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-4">
                            AI-Powered <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Interactivity</span>
                        </h2>
                        <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
                            Ask VISIST questions and get instant visual answers backed by your performance data
                        </p>
                    </motion.div>

                    {/* Interactivity Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Convo.png"
                            alt="Ask VISIST - AI-Powered Conversational Insights"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-700"
                        />
                    </motion.div>

                    {/* Interactivity Features */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                        {interactivityFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-8 hover:border-emerald hover:shadow-2xl transition-all duration-300"
                                >
                                    <div className="w-16 h-16 bg-emerald/20 rounded-xl flex items-center justify-center mb-6">
                                        <Icon className="w-8 h-8 text-emerald" />
                                    </div>
                                    <h3 className="text-2xl font-black text-white mb-4">{feature.title}</h3>
                                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Collaborative Coaching Section */}
            <section className="relative py-24 bg-white overflow-hidden">
                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-sm md:text-base font-bold uppercase tracking-widest mb-3 text-emerald">
                            Bridge the Gap
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
                            Collaborative <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Coaching & Feedback</span>
                        </h2>
                        <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
                            A bridge between coaches and athletes for high-quality scientific interventions
                        </p>
                    </motion.div>

                    {/* Coaching Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Feedback.png"
                            alt="Collaborative Coaching and Feedback Interface"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-200"
                        />
                    </motion.div>

                    {/* Coaching Features */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
                        {coachingFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                                >
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald/10">
                                            <Icon className="w-6 h-6 text-emerald" />
                                        </div>
                                        <h3 className="text-2xl font-black text-gray-900">{feature.title}</h3>
                                    </div>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Coach Quote */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-16 max-w-4xl mx-auto bg-gradient-to-br from-emerald/10 to-white border-2 border-emerald/30 rounded-2xl p-8 md:p-10"
                    >
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-8 h-8 text-emerald flex-shrink-0 mt-1" />
                            <div>
                                <p className="text-gray-700 text-xl italic leading-relaxed mb-4">
                                    "Manual review of 50 students daily is <span className="font-bold text-gray-900">impossible</span>. With Smart Journals, I can make data-backed training decisions without drowning in video footage."
                                </p>
                                <p className="text-gray-600 font-semibold">— Academy Coach</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20 bg-deep-charcoal overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                            Ready to Build Your Performance Library?
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                            Start capturing, analyzing, and learning from every training session
                        </p>
                        <button
                            onClick={() => dispatch(setIsLoginDialog(true))}
                            className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                        >
                            Sign In
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Login Dialog is triggered via Redux dispatch */}
        </div>
    );
};

export default SmartJournals;
